'use client';

import { useState, useEffect } from 'react';

export function useTheme() {
  const [isDark, setIsDark] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Get initial theme from localStorage
      const storedTheme = localStorage.getItem('theme');
      const dark = storedTheme === 'dark';
      setIsDark(dark);
      setIsLoaded(true);

      // Apply theme to document
      if (dark) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }

      // Listen for theme changes in localStorage
      const handleStorageChange = (e: StorageEvent) => {
        if (e.key === 'theme') {
          const newDark = e.newValue === 'dark';
          setIsDark(newDark);
          document.documentElement.classList.toggle('dark', newDark);
        }
      };

      // Listen for class changes on document element
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            const isDarkMode = document.documentElement.classList.contains('dark');
            setIsDark(isDarkMode);
          }
        });
      });

      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class']
      });

      window.addEventListener('storage', handleStorageChange);

      return () => {
        observer.disconnect();
        window.removeEventListener('storage', handleStorageChange);
      };
    }
  }, []);

  const toggleTheme = () => {
    if (typeof window !== 'undefined') {
      const newIsDark = !isDark;
      setIsDark(newIsDark);
      document.documentElement.classList.toggle('dark', newIsDark);
      localStorage.setItem('theme', newIsDark ? 'dark' : 'light');
    }
  };

  return {
    isDark,
    isLoaded,
    toggleTheme,
    theme: isDark ? 'dark' : 'light'
  };
} 